import { InnoveraLoading, Typography } from "@libs/zapi-ui/custom";
import { chapterIcons, defaultChapterIcon } from "@libs/zapi-ui/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { Check } from "@phosphor-icons/react";
import type { TChapterStatusMap } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import type { TChapterMemoStatus } from "@/zapi-frontend/shared/hooks/useChapterMemoPolling";
import { useSearchParams } from "react-router-dom";
import { DashboardProgressIcon } from "./_DashboardProgressIcon";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

type DashboardIslandProps = {
  chapterStatusMap: TChapterStatusMap | null;
  chapterMemoPolling: {
    chapters: TChapterMemoStatus[];
    isPolling: boolean;
  };
};

export function DashboardIsland({ chapterStatusMap, chapterMemoPolling }: DashboardIslandProps) {
  const { selectedProject } = useProjectStore();
  const { projectChapters, setActiveChapter } = useChapterStore();
  const { isLabs } = useMeStore();
  const [, setSearchParams] = useSearchParams();

  const handleChapterClick = (chapterId: string) => {
    setActiveChapter(chapterId);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("chapterId", chapterId);
      params.delete("chapterSubsection");
      return params;
    });
  };

  if (!selectedProject) {
    return (
      <div className={cn("w-full h-full flex flex-col items-center justify-center gap-2")}>
        <InnoveraLoading size={80} />
        <Typography variant="body2" color="zinc500">
          Loading project...
        </Typography>
      </div>
    );
  }

  const getProgressPercentage = (chapter: (typeof projectChapters)[0]) => {
    if (chapter.totalQuestions === 0) return 0;
    return (chapter.validQuestionsCount / chapter.totalQuestions) * 100;
  };

  const getProjectInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const hasAnyDocumentParsing = projectChapters.some((chapter) => {
    const status = chapterStatusMap?.[chapter.id.toString()];
    return status?.phase === "documentParsing";
  });

  console.log(
    projectChapters.map((chapter) => {
      const status = chapterStatusMap?.[chapter.id.toString()];
      return status?.phase;
    })
  );

  const isDocumentParsingPhase = hasAnyDocumentParsing;

  return (
    <div className={cn("w-full h-full flex items-start justify-center p-4 pt-0 overflow-y-auto")}>
      <div className="flex flex-col w-[720px] pb-8">
        <div className="flex-shrink-0 w-full bg-white rounded-[10px]">
          <div className="flex items-center gap-3 pb-[20px] pt-[30px]">
            <div className="flex-shrink-0">
              <div className="size-[44px] bg-[#a553fd] rounded-[5px] flex items-center justify-center p-2">
                <Typography
                  variant="heading4"
                  className="text-white text-[26px] leading-[40px] tracking-[-0.26px] font-medium"
                >
                  {getProjectInitial(selectedProject.name)}
                </Typography>
              </div>
            </div>
            <div className="flex-1 flex flex-col items-start justify-center">
              <Typography variant="heading5" className="text-[20px] leading-[32px] tracking-[-0.2px] font-medium">
                {selectedProject.name}
              </Typography>
              <Typography variant="body2" className="text-[13px] leading-[22px] truncate max-w-[584px]">
                {selectedProject.shortDescription}
              </Typography>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 w-full bg-white rounded-[10px] border border-innoveraZinc-150 relative">
          <div className="border-b border-innoveraZinc-150">
            <div className="flex items-center h-[44px] px-5">
              <div className="flex items-center h-5 w-[251px]">
                <Typography variant="label3" color="zinc500" className="text-[11px] leading-[11px]">
                  Chapter
                </Typography>
              </div>
              {!isDocumentParsingPhase && (
                <div className="flex items-center h-5 flex-1">
                  <Typography variant="label3" color="zinc500" className="text-[11px] leading-[11px]">
                    Task In Progress
                  </Typography>
                </div>
              )}
            </div>
            {isDocumentParsingPhase && (
              <div className="absolute right-[16px] top-[16px] w-[360px] h-[582px] bg-white/80 backdrop-blur-md border border-innoveraZinc-150 rounded-[10px] p-5 flex flex-col items-center justify-center">
                <InnoveraLoading size={80} />
                <Typography variant="label1" className="text-[15px] leading-[15px] mt-6 mb-2 text-center font-medium">
                  Your documents are being analyzed
                </Typography>
                <Typography variant="body2" color="zinc500" className="text-[13px] leading-[22px] text-center">
                  It usually takes 1-3 minutes
                </Typography>
              </div>
            )}
          </div>

          <div className="pb-5 pt-0 px-5">
            {projectChapters
              .filter((chapter) => !chapter.isHidden || isLabs)
              .map((chapter) => {
                const progressPercent = getProgressPercentage(chapter);
                const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === chapter.id);
                const hasActiveMemo = !!chapterMemoStatus?.hasActiveMemo;
                const isStatusLoaded = chapterStatusMap !== null;
                const isComplete = hasActiveMemo && isStatusLoaded;
                const IconComponent = chapter.projectTemplateChapter.icon
                  ? chapterIcons[chapter.projectTemplateChapter.icon]
                  : chapterIcons[defaultChapterIcon];

                const chapterStatus = chapterStatusMap?.[chapter.id.toString()];
                const statusMessage = chapterStatus?.message || chapter.projectTemplateChapter.name;

                return (
                  <div
                    key={chapter.id}
                    className="flex items-center gap-2.5 h-[50px] border-b border-innoveraZinc-150 last:border-b-0"
                  >
                    <div className="flex items-center gap-3 h-5 w-[251px]">
                      <div className="flex items-center justify-center w-8 shrink-0">
                        <div className="relative size-8">
                          <DashboardProgressIcon isComplete={isComplete} progressPercent={progressPercent} />
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                            <IconComponent className="size-4" color="var(--innovera-zinc900)" weight="regular" />
                          </div>
                        </div>
                      </div>
                      <Typography variant="label1" className="text-[15px] leading-[15px]">
                        {chapter.projectTemplateChapter.name}
                      </Typography>
                    </div>

                    {!isDocumentParsingPhase && (
                      <div className="flex items-center gap-3 h-5 flex-1">
                        {isComplete ? (
                          <>
                            <Check className="size-5 shrink-0" color="var(--innovera-green500)" weight="bold" />
                            <Typography variant="body2" color="zinc600" className="text-[13px] leading-[22px]">
                              Complete
                            </Typography>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChapterClick(chapter.id.toString());
                              }}
                              className="ml-auto px-3 py-1 rounded-md text-[13px] leading-[22px] bg-innoveraZinc-100 text-innoveraZinc-900 hover:bg-innoveraZinc-150 transition-colors"
                            >
                              View
                            </button>
                          </>
                        ) : (
                          <>
                            <InnoveraLoading size={20} />
                            <Typography variant="body2" color="zinc600" className="text-[13px] leading-[22px]">
                              {statusMessage}
                            </Typography>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
